import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  isAuthenticated: !!localStorage.getItem("token"),
  isLoading: true,
  loginMessage: null,
  authError: null,
};

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5409";

function reducer(state, action) {
  switch (action.type) {
    case "loading":
      return { ...state, isLoading: true, authError: null };
    case "login":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: !!action.payload.user,
        loginMessage: "Logged In",
        isLoading: false, // Set isLoading=false in login action
        authError: null,
      };
    case "user/created":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: false,
        loginMessage: "Account Created",
        isLoading: false,
        authError: null,
      };
    case "user/updated":
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loginMessage: "Account Updated",
        isLoading: false,
        authError: null,
      };
    case "user/updateProfilePic":
      return {
        ...state,
        user: { ...state.user, ProfilePic: action.payload },
        isAuthenticated: true,
        loginMessage: "Account Updated",
        isLoading: false,
        authError: null,
      };
    case "user/updateProfileMasthead":
      return {
        ...state,
        user: { ...state.user, ProfileMasthead: action.payload },
        isAuthenticated: true,
        loginMessage: "Account Updated",
        isLoading: false,
        authError: null,
      };
    case "logout":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loginMessage: "",
        isLoading: false,
        authError: null,
      };
    case "rejected":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        loginMessage: action.payload.message,
        authError: action.payload.error,
      };
    case "error":
      return {
        ...state,
        isLoading: false,
        loginMessage: action.payload.message,
        authError: action.payload.error,
      };
    default:
      return state;
  }
}

function AuthProvider({ children }) {
  const [
    { user, token, isAuthenticated, loginMessage, isLoading, authError },
    dispatch,
  ] = useReducer(reducer, initialState);

  const isVerifying = useRef(false);

  const verifyToken = useCallback(async (storedToken) => {
    if (isVerifying.current) {
      console.log("verifyToken: Skipping concurrent verification");
      return null;
    }
    isVerifying.current = true;
    try {
      console.log(
        "verifyToken: Checking token",
        storedToken ? storedToken.slice(0, 10) + "..." : "missing",
      );
      const res = await fetch(`${baseUrl}/account/verify-token`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedToken}`,
        },
      });
      const data = await res.json();
      console.log("verifyToken: Response status=", res.status, "data=", data);
      if (!res.ok) {
        throw new Error(
          `HTTP ${res.status}: ${data.message || "Invalid token"}`,
        );
      }
      if (!data.user?.CustUUID) {
        throw new Error("Invalid user data in verifyToken response");
      }
      return {
        ...data.user,
        userId: data.user.CustUUID,
        CustUUID: data.user.CustUUID,
      };
    } catch (err) {
      console.error("verifyToken error:", err.message);
      throw err;
    } finally {
      isVerifying.current = false;
    }
  }, []);

  const refreshToken = useCallback(async () => {
    const storedToken = token || localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (!storedToken) {
      console.error("refreshToken: No token available");
      throw new Error("No token available for refresh");
    }
    try {
      console.log(
        "refreshToken: Attempting to refresh token",
        storedToken.slice(0, 10) + "...",
      );
      const res = await fetch(`${baseUrl}/account/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedToken}`,
        },
      });
      const data = await res.json();
      console.log("refreshToken: Response status=", res.status, "data=", data);
      if (!res.ok) {
        throw new Error(
          `HTTP ${res.status}: ${data.message || "Token refresh failed"}`,
        );
      }
      localStorage.setItem("token", data.token);
      let updatedUser = storedUser ? JSON.parse(storedUser) : null;
      if (updatedUser?.CustUUID) {
        try {
          updatedUser = await fetchUserProfile(
            updatedUser.CustUUID,
            data.token,
          );
        } catch (err) {
          console.error(
            "refreshToken: Failed to fetch user profile:",
            err.message,
          );
        }
      } else {
        console.warn(
          "refreshToken: No CustUUID in stored user, attempting verifyToken",
        );
        updatedUser = await verifyToken(data.token);
      }
      if (!updatedUser?.CustUUID) {
        console.error("refreshToken: No valid user data available");
        throw new Error("Failed to retrieve valid user data");
      }
      dispatch({
        type: "login",
        payload: { user: updatedUser, token: data.token },
      });
      console.log(
        "refreshToken: Success, new token:",
        data.token.slice(0, 10) + "...",
      );
      return data.token;
    } catch (err) {
      console.error("refreshToken error:", err.message);
      dispatch({
        type: "rejected",
        payload: { message: "Token refresh failed", error: err.message },
      });
      throw err;
    }
  }, [verifyToken]);

  async function fetchUserProfile(userId, authToken) {
    dispatch({ type: "loading" });
    try {
      console.log(
        "fetchUserProfile: Fetching URL",
        `${baseUrl}/account/${userId}`,
        "with token",
        authToken.slice(0, 10) + "...",
      );
      const res = await fetch(`${baseUrl}/account/${userId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json();
      console.log(
        "fetchUserProfile: Response status=",
        res.status,
        "data=",
        data,
      );
      if (!res.ok) {
        throw new Error(
          `HTTP ${res.status}: ${data.message || "Unknown error"}`,
        );
      }
      if (!data.user?.CustUUID) {
        throw new Error("Invalid user profile response");
      }
      const normalizedUser = {
        ...data.user,
        userId: data.user.CustUUID,
        CustUUID: data.user.CustUUID,
      };
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      dispatch({
        type: "login",
        payload: { user: normalizedUser, token: authToken },
      });
      return normalizedUser;
    } catch (err) {
      console.error("fetchUserProfile error:", err.message);
      throw err;
    }
  }

  useEffect(() => {
    async function checkAuth() {
      dispatch({ type: "loading" });
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser.CustUUID) {
            throw new Error("Incomplete user data in localStorage");
          }
          console.log(
            "AuthContext: Verifying token for user:",
            parsedUser.CustUUID,
          );
          const verifiedUser = await verifyToken(storedToken);
          if (verifiedUser) {
            dispatch({
              type: "login",
              payload: { user: verifiedUser, token: storedToken },
            });
          } else {
            throw new Error("No valid user data from verifyToken");
          }
        } catch (err) {
          console.log(
            "AuthContext: Token verification failed, attempting refresh:",
            err.message,
          );
          try {
            const newToken = await refreshToken();
            const verifiedUser = await verifyToken(newToken);
            if (verifiedUser) {
              dispatch({
                type: "login",
                payload: { user: verifiedUser, token: newToken },
              });
            } else {
              throw new Error("No valid user data after refresh");
            }
          } catch (refreshErr) {
            console.error("AuthContext: Refresh failed:", refreshErr.message);
            dispatch({
              type: "rejected",
              payload: {
                message: "Authentication failed",
                error: refreshErr.message,
              },
            });
          }
        }
      } else {
        console.log("AuthContext: No token or user in localStorage");
        dispatch({
          type: "rejected",
          payload: {
            message: "No authentication data",
            error: "No token or user",
          },
        });
      }
    }
    checkAuth();
  }, [verifyToken, refreshToken]);

  async function createAccount(newAccount) {
    dispatch({ type: "loading" });
    try {
      const res = await fetch(`${baseUrl}/account/`, {
        method: "POST",
        body: JSON.stringify(newAccount),
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(`HTTP ${res.status}: ${data.message}`);
      }
      const data = await res.json();
      console.log("createAccount response:", data);
      if (data.message === "Account Created") {
        dispatch({ type: "user/created", payload: newAccount });
      } else {
        dispatch({
          type: "rejected",
          payload: { message: data.message, error: data.message },
        });
      }
    } catch (err) {
      console.error("createAccount error:", err.message);
      dispatch({
        type: "rejected",
        payload: {
          message: `Failed to create account: ${err.message}`,
          error: err.message,
        },
      });
    }
  }

  async function updateAccount(theAccount) {
    dispatch({ type: "loading" });
    try {
      const res = await fetchWithToken(`${baseUrl}/account/${user?.CustUUID}`, {
        method: "PATCH",
        body: JSON.stringify(theAccount),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(`HTTP ${res.status}: ${data.message}`);
      }
      const data = await res.json();
      console.log("updateAccount response:", data);
      if (data.message === "Account Updated") {
        const normalizedUser = {
          ...data.user,
          userId: data.user.CustUUID,
          CustUUID: data.user.CustUUID,
        };
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        dispatch({ type: "user/updated", payload: normalizedUser });
      } else {
        dispatch({
          type: "rejected",
          payload: { message: data.message, error: data.message },
        });
      }
    } catch (err) {
      console.error("updateAccount error:", err.message);
      dispatch({
        type: "error",
        payload: {
          message: `Failed to update account: ${err.message}`,
          error: err.message,
        },
      });
    }
  }

  async function updateProfilePic(profilePic) {
    if (!user) {
      throw new Error("No authenticated user available");
    }

    const updatedUser = {
      ...user,
      ProfilePic: profilePic,
      userId: user.CustUUID,
      CustUUID: user.CustUUID,
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    dispatch({
      type: "user/updateProfilePic",
      payload: profilePic,
    });
  }

  async function updateProfileMasthead(profileMasthead) {
    if (!user) {
      throw new Error("No authenticated user available");
    }

    const updatedUser = {
      ...user,
      ProfileMasthead: profileMasthead,
      userId: user.CustUUID,
      CustUUID: user.CustUUID,
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    dispatch({
      type: "user/updateProfileMasthead",
      payload: profileMasthead,
    });
  }

  async function login(email, password) {
    dispatch({ type: "loading" });
    try {
      const loginjson = { Email: email, Password: password };
      const res = await fetch(`${baseUrl}/login/`, {
        method: "POST",
        body: JSON.stringify(loginjson),
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      console.log("login response:", data);
      if (!res.ok) {
        throw new Error(
          `HTTP ${res.status}: ${data.message || "Unknown error"}`,
        );
      }
      if (
        data.Status === "Logged In" &&
        data.UserInfo?.CustUUID &&
        data.token
      ) {
        const normalizedUser = {
          ...data.UserInfo,
          userId: data.UserInfo.CustUUID,
          CustUUID: data.UserInfo.CustUUID,
        };
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        dispatch({
          type: "login",
          payload: { user: normalizedUser, token: data.token },
        });
        await fetchUserProfile(normalizedUser.CustUUID, data.token);
      } else {
        throw new Error(data.Status || "Invalid login response");
      }
    } catch (err) {
      console.error("login error:", err.message);
      dispatch({
        type: "rejected",
        payload: {
          message: `Login failed: ${err.message}`,
          error: err.message,
        },
      });
    }
  }

  function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    dispatch({ type: "logout" });
  }

  const fetchWithToken = useCallback(
    async (url, options = {}) => {
      let authToken = token || localStorage.getItem("token");
      console.log(
        "fetchWithToken: url=",
        url,
        "token=",
        authToken ? authToken.slice(0, 10) + "..." : "missing",
        "userId=",
        user?.CustUUID || "none",
      );
      if (!authToken) {
        console.error("fetchWithToken: No token available");
        dispatch({
          type: "rejected",
          payload: {
            message: "No authentication token available",
            error: "No token",
          },
        });
        throw new Error("No authentication token available");
      }

      try {
        let headers = {
          ...options.headers,
          Authorization: `Bearer ${authToken}`,
        };
        // Remove Content-Type if sending FormData
        if (options.body instanceof FormData && headers["Content-Type"]) {
          delete headers["Content-Type"];
        }
        // If not FormData, default to JSON
        if (!(options.body instanceof FormData) && !headers["Content-Type"]) {
          headers["Content-Type"] = "application/json";
        }
        const res = await fetch(url, { ...options, headers });
        console.log("fetchWithToken: Response status=", res.status);
        if (!res.ok) {
          const readErrorPayload = async (response) => {
            const rawText = await response.text();
            if (!rawText) return {};
            try {
              return JSON.parse(rawText);
            } catch {
              return { message: rawText };
            }
          };

          const data = await readErrorPayload(res);
          console.log("fetchWithToken: Error response=", data);
          if (res.status === 401 && data.message?.includes("expired")) {
            console.log("fetchWithToken: Token expired, attempting refresh");
            try {
              authToken = await refreshToken();
              headers.Authorization = `Bearer ${authToken}`;
              const retryRes = await fetch(url, { ...options, headers });
              console.log("fetchWithToken: Retry status=", retryRes.status);
              if (!retryRes.ok) {
                const retryData = await readErrorPayload(retryRes);
                throw new Error(
                  `HTTP ${retryRes.status}: ${
                    retryData.message || "Unknown error"
                  }`,
                );
              }
              return retryRes;
            } catch (refreshErr) {
              console.error(
                "fetchWithToken: Token refresh failed:",
                refreshErr.message,
              );
              dispatch({
                type: "rejected",
                payload: {
                  message: "Session expired",
                  error: refreshErr.message,
                },
              });
              throw refreshErr;
            }
          }
          throw new Error(
            `HTTP ${res.status}: ${data.message || "Unknown error"}`,
          );
        }
        return res;
      } catch (err) {
        console.error("fetchWithToken error:", err.message);
        throw err;
      }
    },
    [token, user, refreshToken],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        loginMessage,
        authError,
        login,
        createAccount,
        updateAccount,
        updateProfilePic,
        updateProfileMasthead,
        logout,
        fetchWithToken,
        verifyToken,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined)
    throw new Error("AuthContext was used outside AuthProvider");
  return context;
}

export { AuthProvider, useAuth };
